import DatePicker, { DatePickerFormat } from '@app/components/inputs/DatePicker';
import NakedFormInput from '@app/components/inputs/NakedFormInput';
import AutocompleteAddressInput from '@app/components/inputs/AutocompleteAddressInput';
import { AddressComponents } from '@app/hooks';
import { COUNTRY_CODES, SUPPLIER_REGISTRATION_COUNTRY_CODE_BLACKLIST } from '@app/domain/countries';
import config from '@app/config';
import { showCustomModalAsync } from '@app/components/Modal';
import ModalAppContext from '@app/ModalAppContext';
import useAppStores from '@app/hooks/useAppStores';
import useForm from '@app/hooks/useForm';
import useInfraStores from '@app/hooks/useInfraStores';
import useLeaveConfirmation from '@app/hooks/useLeaveConfirmation';
import useModalContext from '@app/hooks/useModalContext';
import { storeUserCountry, useLocaleCountry } from '@app/hooks/useLocaleCountry';
import useTheme from '@app/hooks/useTheme';
import { EVENTS } from '@app/libs/analytics';
import useVendorPortalTrackEvent from '@supplierRegistration/hooks/useVendorPortalTrackEvent';
import { isDefined, isObjectEmpty } from '@app/utils/utils';
import CountryDropdown from '@supplierRegistration/components/CountryDropdown';
import HideableSupplierRegistrationFormItemBox from '@supplierRegistration/components/HideableSupplierRegistrationFormItemBox';
import LEITypeFormItem from '@supplierRegistration/components/LEITypeFormItem';
import PhoneNumberInput from '@supplierRegistration/components/PhoneNumberInput';
import PleaseNote from '@supplierRegistration/components/PleaseNote';
import ReferringOrg from '@supplierRegistration/components/ReferringOrg';
import StateDropdown from '@supplierRegistration/components/StateDropdown';
import SupplierRegistrationFormItemBox from '@supplierRegistration/components/SupplierRegistrationFormItemBox';
import * as countryProperties from '@supplierRegistration/domain/countries';
import {
  RegistrationType,
  SupplierRegistrationCompanyInfoFormFields,
  SupplierValidationRegistrationProps,
} from '@supplierRegistration/domain/supplierRegistration';
import { createFieldDecorators } from '@supplierRegistration/domain/supplierRegistrationCompanyInfoDecorators';
import {
  AllLegalIdTypeForCompany,
  getLegalIdTypesOfCountryForCompany,
} from '@supplierRegistration/domain/supplierRegistrationLegalIdentifiers';
import { guestSession } from '@supplierRegistration/services/supplierRegistrationGuestSessionService';
import { SupplierDocumentType } from '@supplierRegistration/stores/appStores/SupplierRegistrationDocumentsStore';
import ContinueWithoutDocumentsModal from '@supplierRegistration/routes/supplierRegistrationProcess/documentUpload/ContinueWithoutDocumentsModal';
import DocumentUploadSection from '@supplierRegistration/routes/supplierRegistrationProcess/documentUpload/DocumentUploadSection';
import EmailVerificationModal from '@supplierRegistration/routes/supplierRegistrationProcess/documentUpload/EmailVerificationModal';
import SupplierRegistrationMode from '@supplierRegistration/supplierRegistrationMode';
import { Form } from 'antd';
import { FormComponentProps } from 'antd/es/form/Form';
import { observer, useLocalObservable } from 'mobx-react';
import React, { FC, ReactNode, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import {
  CardSubTitle,
  CardTitle,
  MainCard,
  SeparatorDiv,
  SmallContinueButton,
  SmallSeparator,
  SmallSeparatorWithExtraMargin,
  StyledForm,
} from '../../Styles';
import {
  DateOfBirth,
  InputGrid,
  RadioOptionLabel,
  StyledFormRadioGroup,
  TransparentSupplierRegistrationFormItemBox,
} from './SupplierRegistrationCompanyInformation.styles';
import CanadaProvincesDropdown from '@supplierRegistration/components/CanadaProvincesDropdown';

const monthsTranslations: string[] = [
  'general.months.january',
  'general.months.february',
  'general.months.march',
  'general.months.april',
  'general.months.may',
  'general.months.june',
  'general.months.july',
  'general.months.august',
  'general.months.september',
  'general.months.october',
  'general.months.november',
  'general.months.december',
];

interface Props extends FormComponentProps<SupplierRegistrationCompanyInfoFormFields>, SupplierValidationRegistrationProps {}

const SupplierRegistrationCompanyInformation: FC<Props> = observer((props) => {
  useLeaveConfirmation();

  const { configuration, formValues, onFormSubmit, isIpsMode, nextStep } = props;
  const { form, showFormErrors, setShowFormErrors } = useForm(props);
  const { t } = useTranslation();
  const { languageStore } = useInfraStores<SupplierRegistrationMode>();
  const { supplierRegistrationCustomerDataStore, supplierRegistrationDocumentsStore } = useAppStores<SupplierRegistrationMode>();
  const trackEvent = useVendorPortalTrackEvent();
  const { brand, continueButtonColor, fontFamily } = useTheme<SupplierRegistrationMode>();
  const modalContext = useModalContext();
  const [isVerificationModalOpen, setIsVerificationModalOpen] = useState(false);

  const customerName = supplierRegistrationCustomerDataStore.customerName;
  const supplierCountryCode = form.getFieldValue('country');
  const addressCountryCode = form.getFieldValue('companyAddressCountry');
  const leiType = form.getFieldValue('leiType');
  const legalIdTypesOfCountry = getLegalIdTypesOfCountryForCompany(supplierCountryCode);
  const isAsync = supplierRegistrationCustomerDataStore.currentAsyncProcessMaskedData?.isResolved();

  useLocaleCountry(
    (userCountry, userCountryCode): false | number =>
      !formValues.supplierValues?.phoneCountryCode &&
      window.setTimeout((): void => form.setFieldsValue({ phoneCountryCode: userCountryCode }), 0),
  );

  const onLeiTypeChange = (leiTypeKey: string): void => {
    localStore.setIsOtherLEIType(AllLegalIdTypeForCompany[leiTypeKey] === AllLegalIdTypeForCompany.Other);
    form.validateFields();
  };

  const isIndividual = (): boolean => {
    return form.getFieldValue('registrationType') === RegistrationType.individual;
  };

  const showDateOfBirth = (): boolean => {
    return isIndividual() && !!supplierCountryCode && countryProperties?.[supplierCountryCode]?.showDateOfBirth;
  };

  const showAdditionalPersonalName = (): boolean => {
    return isIndividual() && !!supplierCountryCode && countryProperties?.[supplierCountryCode]?.additionalPersonalName;
  };

  const showAdditionalCompanyName = (): boolean => {
    return !isIndividual() && !!supplierCountryCode && countryProperties?.[supplierCountryCode]?.additionalCompanyName;
  };

  const doesSupplierCountryHasAdditionalName = showAdditionalPersonalName() || showAdditionalCompanyName();

  const asyncProcessMaskedData = supplierRegistrationCustomerDataStore.currentAsyncProcessMaskedData?.result;

  const isDefinedAddressLine = isDefined(asyncProcessMaskedData?.payee?.addressByFields?.addressLine);
  const addressLine = asyncProcessMaskedData?.payee?.addressByFields?.addressLine ?? undefined;
  const isDefinedCity = isDefined(asyncProcessMaskedData?.payee?.addressByFields?.city);
  const city = asyncProcessMaskedData?.payee?.addressByFields?.city ?? undefined;

  const hasAsyncPrefilledEmail = isDefined(asyncProcessMaskedData?.contactInfo.email);
  // Keyed on validity (hasAnyEmptyDocumentSection), not row presence: a section holding only
  // client- or server-rejected rows has nothing eligible to submit and must trigger this same
  // confirmation, or a resubmission after a 422 would silently complete the registration with that
  // type's documents missing.
  const hasEmptyDocumentSection = supplierRegistrationDocumentsStore.hasAnyEmptyDocumentSection;

  useEffect((): void => {
    form.resetFields(['leiType']);
    localStore.setIsOtherLEIType(!!formValues.supplierValues?.leiTypeOtherValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- form is a different instance every time
  }, [legalIdTypesOfCountry]);

  useEffect((): void => {
    if (doesSupplierCountryHasAdditionalName) {
      form.resetFields(['additionalCompanyName']);
    }
    form.validateFields();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- form is a different instance every time
  }, [doesSupplierCountryHasAdditionalName]);

  const localStore = useLocalObservable(() => ({
    isOtherLEIType: !!formValues.supplierValues?.leiTypeOtherValue,

    setIsOtherLEIType: (isOtherLEIType: boolean): void => {
      localStore.isOtherLEIType = isOtherLEIType;
    },
  }));

  const handleRegistrationTypeChange = (): void => {
    form.resetFields(Object.keys(fieldDecorators).filter((field) => field !== 'registrationType'));

    // email isn't itself exempted from the reset above -- a verified, locked email must survive
    // the Company/Individual toggle the same way the reset-proof documents store does, or the
    // supplier is left facing a required, disabled, now-empty field they have no way to fill in.
    if (guestSession.isVerified && guestSession.verifiedEmail && !hasAsyncPrefilledEmail) {
      form.setFieldsValue({ email: guestSession.verifiedEmail });
    }
  };

  const handleBeforeAttach = (): boolean => {
    if (guestSession.isVerified) {
      return true;
    }

    setIsVerificationModalOpen(true);
    return false;
  };

  const handleEmailVerified = (verifiedEmail: string): void => {
    setIsVerificationModalOpen(false);

    // The async-prefilled email (if any) is authoritative and already locked via hasPrefilledData
    // -- the verified value is only meant to fill in an email the supplier is otherwise entering
    // themselves, never to override what the payer already supplied.
    if (!hasAsyncPrefilledEmail) {
      form.setFieldsValue({ email: verifiedEmail });
    }
  };

  // Applies an already-verified session's email independently of the verification modal ever
  // opening. guestSession.init() (run unconditionally by EmailVerificationModal's own mount
  // effect) can restore a prior verification -- flipping isVerified to true -- before the supplier
  // ever clicks attach; handleEmailVerified above is only reachable through the modal's onVerified
  // callback, which never fires while the modal stays closed. Without this, a returning,
  // already-verified supplier would see the (correctly) disabled email field left empty, with no
  // way to fill it back in. Safe to run again after handleEmailVerified already wrote the same
  // value -- setFieldsValue with an unchanged value is a no-op.
  useEffect(() => {
    if (guestSession.isVerified && guestSession.verifiedEmail && !hasAsyncPrefilledEmail) {
      form.setFieldsValue({ email: guestSession.verifiedEmail });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- form is a different instance every time
  }, [guestSession.isVerified, guestSession.verifiedEmail, hasAsyncPrefilledEmail]);

  const showContinueWithoutDocumentsModal = async (): Promise<boolean> => {
    let shouldContinue = false;

    await showCustomModalAsync(
      (onDone) => (
        <ModalAppContext {...modalContext}>
          <ContinueWithoutDocumentsModal
            onDone={(result: boolean): void => {
              shouldContinue = result;
              onDone();
            }}
          />
        </ModalAppContext>
      ),
      { maskClosable: false },
    );

    return shouldContinue;
  };

  const handleOk = (): void => {
    // The pre-form email-verification step now gates every entry to this page while the flag is
    // on, so reaching submit unverified should be rare -- but an EMAIL_MISMATCH reset (which clears
    // the session and routes back here) can still land here unverified with no file changes since,
    // and previously this only re-checked when files were attached. Gate unconditionally, or a
    // submit with no files (or files removed after the reset) would silently proceed with no guest
    // identity.
    if (config.featureFlags?.supplierRegistrationDocuments && !guestSession.isVerified) {
      setIsVerificationModalOpen(true);
      return;
    }

    const { validateFieldsAndScroll } = form;
    setShowFormErrors('all');

    validateFieldsAndScroll(async (errors: Object, values: SupplierRegistrationCompanyInfoFormFields) => {
      if (errors) {
        const errorKeys = Object.keys(errors);
        trackEvent({
          name: EVENTS.VENDOR_PORTAL_PAYEE_DETAILS_ERROR,
          ref_id: configuration?.ref ?? undefined,
          is_brand_page: !!brand,
          is_ips_mode: isIpsMode,
          error_message: errorKeys.length > 0 ? `Invalid fields: ${errorKeys.join(', ')}` : 'Invalid details',
        });
        return;
      }

      if (config.featureFlags?.supplierRegistrationDocuments && hasEmptyDocumentSection) {
        const shouldContinue = await showContinueWithoutDocumentsModal();
        if (!shouldContinue) {
          return;
        }
      }

      onFormSubmit(values);
      storeUserCountry(values.phoneCountryCode);
      nextStep();
    });
  };

  const onSupplierCountryChange = (countryCode: string | undefined): void => {
    form.setFieldsValue({
      phoneCountryCode: form.getFieldValue('phoneNumber') ? form.getFieldValue('phoneCountryCode') : countryCode,
    });
    form.setFieldsValue({ companyAddressCountry: form.getFieldValue('companyAddressCountry') ?? countryCode });
  };

  const handlePlaceSelect = (place: any, addressComponents: AddressComponents | null): void => {
    if (!addressComponents) {
      // Fallback to old behavior if no address components
      if (!place?.placePrediction?.mainText?.text) return;

      const { mainText, secondaryText } = place.placePrediction;
      const addressParts = [mainText.text];

      if (secondaryText?.text?.trim()) {
        addressParts.push(secondaryText.text);
      }

      form.setFieldsValue({
        companyAddress: addressParts.join(', '),
      });
      return;
    }

    // Use the parsed address components to populate all fields
    form.setFieldsValue({
      companyAddress:
        addressComponents.streetAddress || addressComponents.formattedAddress || form.getFieldValue('companyAddress'),
      companyAddressCity: addressComponents.city || form.getFieldValue('companyAddressCity'),
      companyAddressState: addressComponents.state || form.getFieldValue('companyAddressState'),
      companyAddressZipCode: addressComponents.postalCode || form.getFieldValue('companyAddressZipCode'),
    });

    // Update current city state variable
    if (addressComponents.city) {
      setCurrentCity(addressComponents.city);
    }
  };

  const fieldDecorators = createFieldDecorators(
    form,
    asyncProcessMaskedData,
    isIndividual(),
    supplierCountryCode,
    addressCountryCode,
    formValues,
    showDateOfBirth(),
    leiType,
  );

  function getCardSubTitle(): ReactNode {
    if (isIpsMode) {
      return <>{t('supplierValidation.registerSteps.companyInformation.subTitleIps')}</>;
    }

    if (isAsync) {
      return (
        <Trans i18nKey='supplierValidation.registerSteps.companyInformation.subTitleAsync' values={{ customerName }}>
          Complete missing company details. Details already provided to {{ customerName }} are prefilled. To modify any of these,
          please contact {{ customerName }}.
        </Trans>
      );
    }

    return (
      <Trans i18nKey='supplierValidation.registerSteps.companyInformation.subTitle'>
        Enter your company details that you have <strong>provided to the payer</strong>
      </Trans>
    );
  }

  const isAddressCountryUS = form.getFieldValue('companyAddressCountry') === COUNTRY_CODES.UnitedStates;
  const isAddressCountryCanada = form.getFieldValue('companyAddressCountry') === COUNTRY_CODES.Canada;

  const [currentCity, setCurrentCity] = useState<string | undefined>(form.getFieldValue('companyAddressCity'));
  const companyAddressCityValue = form.getFieldValue('companyAddressCity');
  useEffect(() => {
    setCurrentCity(companyAddressCityValue);
  }, [companyAddressCityValue]);

  const handleCitySelect = (place: any, addressComponents: AddressComponents | null): void => {
    if (!addressComponents && place && place.placePrediction?.mainText?.text) {
      const cityValue = place.placePrediction?.mainText?.text;
      form.setFieldsValue({ companyAddressCity: cityValue });
      setCurrentCity(cityValue); // Update the state variable
    } else if (addressComponents) {
      form.setFieldsValue({ companyAddressCity: addressComponents.city, companyAddressState: addressComponents.state });
      setCurrentCity(addressComponents.city);
    }
  };

  return (
    <>
      <MainCard fontFamily={fontFamily}>
        <StyledForm onSubmit={handleOk} showErrors={showFormErrors} form={form} setShowErrors={setShowFormErrors}>
          {configuration?.clientName && !brand && <ReferringOrg orgName={configuration.clientName} />}
          <CardTitle>
            {isAsync
              ? t('supplierValidation.registerSteps.companyInformation.titleAsync')
              : t('supplierValidation.registerSteps.companyInformation.title')}
          </CardTitle>
          <CardSubTitle>{getCardSubTitle()}</CardSubTitle>
          {config.featureFlags?.supplierRegistrationDocuments && (
            <>
              <DocumentUploadSection type={SupplierDocumentType.bankAccountDocumentation} onBeforeAttach={handleBeforeAttach} />
              <DocumentUploadSection type={SupplierDocumentType.taxIdCertificate} onBeforeAttach={handleBeforeAttach} />
              <EmailVerificationModal
                open={isVerificationModalOpen}
                onVerified={handleEmailVerified}
                onClose={(): void => setIsVerificationModalOpen(false)}
              />
            </>
          )}
          <SupplierRegistrationFormItemBox
            appearance='none'
            fieldName='registrationType'
            fieldDecoratorOptions={fieldDecorators.registrationType}
            hasPrefilledData={isDefined(asyncProcessMaskedData?.payee.isIndividual)}
            onChange={handleRegistrationTypeChange}
          >
            <StyledFormRadioGroup
              accessibilityLabel={t('supplierValidation.accessibility.registrationType')}
              id='radio-group-supp-reg-registration-type'
              dataTestId='radio-group-supp-reg-registration-type'
              options={[
                {
                  value: RegistrationType.company,
                  label: <RadioOptionLabel>{t('supplierValidation.supplierRegister.company')}</RadioOptionLabel>,
                },
                {
                  value: RegistrationType.individual,
                  label: <RadioOptionLabel>{t('supplierValidation.supplierRegister.individual')}</RadioOptionLabel>,
                },
              ]}
              direction='column'
            />
          </SupplierRegistrationFormItemBox>
          <SmallSeparatorWithExtraMargin />
          <InputGrid>
            <SupplierRegistrationFormItemBox
              fieldName='country'
              fieldDecoratorOptions={fieldDecorators.country}
              hasPrefilledData={isDefined(asyncProcessMaskedData?.payee.countryCode)}
            >
              <CountryDropdown
                name='drp-supp-reg-supplier-country'
                placeholder={t<string>(
                  isIndividual()
                    ? 'supplierValidation.supplierRegister.country'
                    : 'supplierValidation.supplierRegister.companyCountry',
                )}
                excludedCountries={SUPPLIER_REGISTRATION_COUNTRY_CODE_BLACKLIST}
                onChange={onSupplierCountryChange}
              />
            </SupplierRegistrationFormItemBox>
          </InputGrid>
          {showDateOfBirth() && (
            <>
              <SeparatorDiv>
                <SmallSeparator />
                {t(`supplierValidation.supplierRegister.dateOfBirthHeader`)}
              </SeparatorDiv>
              <InputGrid>
                <DateOfBirth>
                  <SupplierRegistrationFormItemBox
                    fieldName='dateOfBirth'
                    fieldDecoratorOptions={fieldDecorators.dateOfBirth}
                    appearance='none'
                    hasPrefilledData={false}
                  >
                    <DatePicker
                      appearance='corners'
                      accessibilityLabel={t(`supplierValidation.supplierRegister.dateOfBirthHeader`)}
                      id='date-picker-birth-of-date'
                      placeholderStyle='onlyWhenEmpty'
                      heightType='thin'
                      dateFormat={DatePickerFormat.YYMMDD}
                      bottomYearLimit={150}
                      translatedMonths={monthsTranslations}
                    />
                  </SupplierRegistrationFormItemBox>
                </DateOfBirth>
              </InputGrid>
            </>
          )}
          <SeparatorDiv>
            <SmallSeparator />
            {t(
              isIndividual()
                ? 'supplierValidation.supplierRegister.individualInfoHeader'
                : 'supplierValidation.supplierRegister.companyInfoHeader',
            )}
          </SeparatorDiv>
          <InputGrid>
            <SupplierRegistrationFormItemBox
              fieldName='companyName'
              fieldDecoratorOptions={fieldDecorators.companyName}
              hasPrefilledData={isDefined(asyncProcessMaskedData?.payee.name)}
            >
              <NakedFormInput
                name='btn-supp-reg-company-name'
                dataTestId='btn-supp-reg-company-name'
                type='text'
                placeholder={t<string>(
                  isIndividual()
                    ? [
                        `supplierValidation.supplierRegister.${supplierCountryCode}.accountOwnerName`,
                        'supplierValidation.supplierRegister.accountOwnerName',
                      ]
                    : [
                        `supplierValidation.supplierRegister.${supplierCountryCode}.compName`,
                        'supplierValidation.supplierRegister.compName',
                      ],
                )}
                disableSuggestion
              />
            </SupplierRegistrationFormItemBox>
            {(doesSupplierCountryHasAdditionalName || showAdditionalPersonalName()) && (
              <SupplierRegistrationFormItemBox
                fieldName='additionalCompanyName'
                fieldDecoratorOptions={fieldDecorators.additionalCompanyName}
                hasPrefilledData={isDefined(asyncProcessMaskedData?.payee.additionalCompanyName)}
              >
                <NakedFormInput
                  name='btn-supp-reg-additional-company-name'
                  dataTestId='btn-supp-reg-additional-company-name'
                  type='text'
                  placeholder={t<string>(
                    isIndividual()
                      ? [
                          `supplierValidation.supplierRegister.${supplierCountryCode}.additionalPersonalName`,
                          'supplierValidation.supplierRegister.additionalPersonalName',
                        ]
                      : [
                          `supplierValidation.supplierRegister.${supplierCountryCode}.additionalCompanyName`,
                          'supplierValidation.supplierRegister.additionalCompanyName',
                        ],
                  )}
                  disableSuggestion
                />
              </SupplierRegistrationFormItemBox>
            )}
            <LEITypeFormItem
              form={form}
              fieldDecorators={fieldDecorators}
              isIndividual={isIndividual()}
              showFormErrors={showFormErrors}
              asyncProcessMaskedData={asyncProcessMaskedData}
              legalIdTypesOfCountry={legalIdTypesOfCountry}
              isOtherLEIType={localStore.isOtherLEIType}
              onLeiTypeChange={onLeiTypeChange}
              areChildrenRTL={languageStore.isRTL}
            />
            <SupplierRegistrationFormItemBox
              fieldName='leiValue'
              fieldDecoratorOptions={fieldDecorators.leiValue}
              hasPrefilledData={isDefined(asyncProcessMaskedData?.payee.legalIdentifier)}
            >
              <NakedFormInput
                name='btn-supp-reg-lei-value'
                dataTestId='btn-supp-reg-lei-value'
                type='text'
                placeholder={
                  isIndividual()
                    ? t<string>([
                        `supplierValidation.supplierRegister.individualForm.${supplierCountryCode}.legalId`,
                        'supplierValidation.supplierRegister.legalId',
                      ])
                    : t<string>('supplierValidation.supplierRegister.legalId')
                }
                disableSuggestion
              />
            </SupplierRegistrationFormItemBox>
            {!configuration?.ref && !isIpsMode && (
              <SupplierRegistrationFormItemBox
                fieldName='referrer'
                fieldDecoratorOptions={fieldDecorators.referrer}
                hasPrefilledData={false}
              >
                <NakedFormInput
                  name='btn-supp-reg-referrer'
                  dataTestId='btn-supp-reg-referrer'
                  type='text'
                  placeholder={t<string>('supplierValidation.supplierRegister.referrer')}
                  disableSuggestion
                />
              </SupplierRegistrationFormItemBox>
            )}
            <SupplierRegistrationFormItemBox
              fieldName='website'
              fieldDecoratorOptions={fieldDecorators.website}
              hasPrefilledData={isDefined(asyncProcessMaskedData?.payee.website)}
            >
              <NakedFormInput
                name='btn-supp-reg-website'
                dataTestId='btn-supp-reg-website'
                type='text'
                placeholder={t<string>('supplierValidation.supplierRegister.website')}
                disableSuggestion
              />
            </SupplierRegistrationFormItemBox>
          </InputGrid>
          <SeparatorDiv>
            <SmallSeparator />
            {t(`supplierValidation.supplierRegister.addressHeader`)}
          </SeparatorDiv>
          <InputGrid>
            {!isDefined(asyncProcessMaskedData?.payee.address) ||
            !isObjectEmpty(asyncProcessMaskedData?.payee.addressByFields) ? (
              <>
                <SupplierRegistrationFormItemBox
                  fieldName='companyAddressCountry'
                  fieldDecoratorOptions={fieldDecorators.companyAddressCountry}
                  hasPrefilledData={isDefined(asyncProcessMaskedData?.payee?.addressByFields?.countryCode)}
                >
                  <CountryDropdown
                    name='drp-supp-reg-company-address-country'
                    placeholder={t<string>('supplierValidation.supplierRegister.addressCountry')}
                    excludedCountries={SUPPLIER_REGISTRATION_COUNTRY_CODE_BLACKLIST}
                    onChange={(value): void => {
                      // Reset the state field whenever country changes
                      form.setFieldsValue({ companyAddressState: '' });
                    }}
                  />
                </SupplierRegistrationFormItemBox>
                <SupplierRegistrationFormItemBox
                  fieldName='companyAddress'
                  fieldDecoratorOptions={fieldDecorators.companyAddress}
                  hasPrefilledData={isDefinedAddressLine}
                >
                  <AutocompleteAddressInput
                    name='btn-supp-reg-company-address'
                    dataTestId='btn-supp-reg-company-address'
                    value={addressLine || (form.getFieldValue('companyAddress') as string) || ''}
                    placeholder={'Company address'}
                    disabled={!addressCountryCode || addressCountryCode === '' || !!addressLine}
                    onPlaceSelect={handlePlaceSelect}
                    componentRestrictions={addressCountryCode ? { country: addressCountryCode } : undefined}
                    state={form.getFieldValue('companyAddressState') || undefined}
                    city={currentCity}
                  />
                </SupplierRegistrationFormItemBox>
                <SupplierRegistrationFormItemBox
                  fieldName='companyAddressCity'
                  fieldDecoratorOptions={fieldDecorators.companyAddressCity}
                  hasPrefilledData={isDefinedCity}
                >
                  <AutocompleteAddressInput
                    name='btn-supp-reg-company-address-city'
                    dataTestId='btn-supp-reg-company-address-city'
                    placeholder={'Company city'}
                    value={city || currentCity || form.getFieldValue('companyAddressCity')}
                    disabled={!addressCountryCode || addressCountryCode === '' || !!city}
                    onPlaceSelect={handleCitySelect}
                    componentRestrictions={addressCountryCode ? { country: addressCountryCode } : undefined}
                    types={['(cities)']}
                    state={form.getFieldValue('companyAddressState') || undefined}
                  />
                </SupplierRegistrationFormItemBox>
                <TransparentSupplierRegistrationFormItemBox
                  shouldBeHidden={!isAddressCountryUS && !isAddressCountryCanada}
                  fieldName='companyAddressState'
                  fieldDecoratorOptions={fieldDecorators.companyAddressState}
                  hasPrefilledData={isDefined(asyncProcessMaskedData?.payee?.addressByFields?.state)}
                >
                  {isAddressCountryCanada ? (
                    <CanadaProvincesDropdown
                      name='drp-supp-reg-company-address-state'
                      placeholder={`${t<string>('supplierValidation.supplierRegister.addressProvince')}`}
                    />
                  ) : (
                    <StateDropdown
                      name='drp-supp-reg-company-address-state'
                      placeholder={`${t<string>('supplierValidation.supplierRegister.addressState')}`}
                    />
                  )}
                </TransparentSupplierRegistrationFormItemBox>
                <SupplierRegistrationFormItemBox
                  fieldName='companyAddressZipCode'
                  fieldDecoratorOptions={fieldDecorators.companyAddressZipCode}
                  hasPrefilledData={isDefined(asyncProcessMaskedData?.payee?.addressByFields?.zipCode)}
                >
                  <NakedFormInput
                    name='btn-supp-reg-company-address-zipcode'
                    dataTestId='btn-supp-reg-company-address-zipcode'
                    type='text'
                    placeholder={t<string>('supplierValidation.supplierRegister.addressZipCode')}
                    disableSuggestion
                    disabled={!addressCountryCode}
                  />
                </SupplierRegistrationFormItemBox>
              </>
            ) : (
              <SupplierRegistrationFormItemBox
                fieldName='companyAddress'
                fieldDecoratorOptions={fieldDecorators.companyAddress}
                hasPrefilledData={true}
              >
                <AutocompleteAddressInput
                  name='btn-supp-reg-company-address'
                  dataTestId='btn-supp-reg-company-address'
                  placeholder={t<string>(
                    isIndividual()
                      ? 'supplierValidation.supplierRegister.address'
                      : [
                          `supplierValidation.supplierRegister.${supplierCountryCode}.companyAddress`,
                          'supplierValidation.supplierRegister.companyAddress',
                        ],
                  )}
                  value={addressLine ? addressLine : ''}
                  onPlaceSelect={handlePlaceSelect}
                  disabled={!addressCountryCode || addressCountryCode === '' || !!addressLine}
                  componentRestrictions={addressCountryCode ? { country: addressCountryCode } : undefined}
                  state={form.getFieldValue('companyAddressState') || undefined}
                  city={currentCity}
                />
              </SupplierRegistrationFormItemBox>
            )}
          </InputGrid>
          <SeparatorDiv>
            <SmallSeparator />
            {t(`supplierValidation.supplierRegister.contactInfoHeader`)}
          </SeparatorDiv>
          <InputGrid>
            <HideableSupplierRegistrationFormItemBox
              fieldName='asyncFullName'
              fieldDecoratorOptions={fieldDecorators.asyncFullName}
              hasPrefilledData={isDefined(asyncProcessMaskedData?.contactInfo.asyncFullName)}
              hidden={
                !isDefined(asyncProcessMaskedData?.contactInfo.asyncFullName) ||
                isDefined(asyncProcessMaskedData?.contactInfo.firstName)
              }
            >
              <NakedFormInput
                name='btn-supp-reg-async-full-name'
                dataTestId='btn-supp-reg-async-full-name'
                type='text'
                placeholder={t<string>('supplierValidation.supplierRegister.contactName')}
                disableSuggestion
              />
            </HideableSupplierRegistrationFormItemBox>
            <HideableSupplierRegistrationFormItemBox
              fieldName='firstName'
              fieldDecoratorOptions={fieldDecorators.firstName}
              hasPrefilledData={isDefined(asyncProcessMaskedData?.contactInfo.firstName)}
              hidden={
                !isDefined(asyncProcessMaskedData?.contactInfo.firstName) &&
                isDefined(asyncProcessMaskedData?.contactInfo.asyncFullName)
              }
            >
              <NakedFormInput
                name='btn-supp-reg-first-name'
                dataTestId='btn-supp-reg-first-name'
                type='text'
                placeholder={t<string>('supplierValidation.supplierRegister.contactFirstName')}
                disableSuggestion
              />
            </HideableSupplierRegistrationFormItemBox>
            <HideableSupplierRegistrationFormItemBox
              fieldName='lastName'
              fieldDecoratorOptions={fieldDecorators.lastName}
              hasPrefilledData={isDefined(asyncProcessMaskedData?.contactInfo.lastName)}
              hidden={
                !isDefined(asyncProcessMaskedData?.contactInfo.lastName) &&
                isDefined(asyncProcessMaskedData?.contactInfo.asyncFullName)
              }
            >
              <NakedFormInput
                name='btn-supp-reg-last-name'
                dataTestId='btn-supp-reg-last-name'
                type='text'
                placeholder={t<string>('supplierValidation.supplierRegister.contactLastName')}
                disableSuggestion
              />
            </HideableSupplierRegistrationFormItemBox>
            <PhoneNumberInput
              hasAsyncMaskedDataPhoneNumber={!!asyncProcessMaskedData?.contactInfo.phoneNumber}
              fieldDecorators={fieldDecorators}
            />
            <SupplierRegistrationFormItemBox
              fieldName='email'
              fieldDecoratorOptions={fieldDecorators.email}
              hasPrefilledData={hasAsyncPrefilledEmail}
              disabled={guestSession.isVerified}
            >
              <NakedFormInput
                name='inpt-auth-forgot-enter-email'
                dataTestId='inpt-auth-forgot-enter-email'
                type='text'
                placeholder={t<string>('supplierValidation.supplierRegister.mail')}
                disableSuggestion
              />
            </SupplierRegistrationFormItemBox>
          </InputGrid>
          <SmallContinueButton id='btn-supp-reg-company-info-continue' onClick={handleOk} colorScheme={continueButtonColor}>
            {t('supplierValidation.supplierRegister.continue')}
          </SmallContinueButton>
        </StyledForm>
      </MainCard>
      <PleaseNote isIpsMode={isIpsMode} configuration={configuration} />
    </>
  );
});

export default Form.create<Props>()(SupplierRegistrationCompanyInformation);
